import { signal } from '@preact/signals-react';
import { calendarScrollTo } from 'Calendar';
import calcTo from 'Calendar/helper';
import { isModeFull } from 'Calendar/styles';
import { DatePicker as DatePickerAnt, Row } from 'antd';
import type { Moment, Moment as MomentType } from 'moment';
import moment from 'utils/moment';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import React, { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import aptActions from 'services/actions/apt';
import { useAppDispatch } from 'store/hooks';
import styled, { css } from 'styled-components';
import getRangeDates, { IDateRangeItem } from 'utils/getRangeDates';
import IconCalendar from '../assets/Icons/iconDate';
import { aptUIActions } from 'services/reducers/apt';
import { FILTER_INPUT_HEIGHT, FILTER_INPUT_FONT_SIZE, filterInputBox, filterInputText } from './filter-input-style';

const handleScrollDate = (dateValue: moment.Moment | null) => {
  if (!dateValue) return calendarScrollTo.value(0);
  let pixel = 0;
  if (dateValue.isSame(moment(), 'date')) {
    pixel = calcTo(moment().format('MM-DD-YYYY HH:mm:ss'));
  }
  calendarScrollTo.value(pixel);
};

const DatePickerAntUI = DatePickerAnt.generatePicker<MomentType>(momentGenerateConfig);

export const dateParamSignal = signal<MomentType | null | undefined>(moment());
const _dates = getRangeDates(moment().format(), moment()?.clone().add(1, 'month').format());
type DatePickerRef = {
  setSelectedDate: (data: MomentType | null) => void;
  /**
   * Chuyen lich sang mot ngay khac tu ben ngoai, chay dung duong ma nguoi dung bam:
   * doi ngay dang chon, tai lai du lieu, va keo dai ngay ve dung vi tri.
   */
  goToDate: (date: MomentType) => void;
};
export const calendarDatePickerRef = React.createRef<DatePickerRef>();
type DatePickerProps = {
  /** Chi hien o chon ngay, bo dai ngay cuon ngang — dung khi nhet vao thanh filter chat cho. */
  compact?: boolean;
};
const DatePicker = forwardRef<DatePickerRef, DatePickerProps>(({ compact }, ref) => {
  const dispatch = useAppDispatch();
  const [selectedDate, setSelectedDate] = useState<MomentType | null>(dateParamSignal.value || moment());
  const [dates, setDates] = useState<IDateRangeItem[]>(_dates);
  const datesListRef = useRef<HTMLDivElement>(null);

  const datesString = useMemo(() => dates.map(o => o.id), [dates]);

  const afterChangeDate = (d: moment.Moment | null) => {
    dispatch(aptActions.calendar.getData.fetch({ startTime: d?.format('YYYY-MM-DD') || '' }));
    if (!d) return;
    handleScrollDate(d);
    if (datesListRef.current) {
      const element = datesListRef.current.querySelector(`#BoxTimeDate_${d?.format('DD-MM-YYYY')}`);
      if (element) {
        datesListRef.current.scrollTo({
          left: (element as HTMLElement).offsetLeft,
          behavior: 'smooth',
        });
      }
    }
  };

  /**
   * Chot ngay cua calendar. Phai chay dong bo va chay TRUOC khi dispatch request
   * cua ngay moi.
   *
   * Hoan lai se tao mot khoang ma `dateParamSignal` con la ngay cu trong khi
   * request dang tai ngay moi. Mot su kien socket roi vao khoang do se dispatch
   * request cho ngay cu, `takeLatest` huy luon request cua ngay moi, va luoi hien
   * trong cho toi lan refresh tay tiep theo.
   *
   * Dat truoc dispatch vi saga chay dong bo toi `yield call` dau tien: bat ky
   * saga nao doc thang `dateParamSignal` thay vi `payload.startTime` deu phai
   * thay ngay moi.
   */
  const setCalendarDate = (d: Moment | null) => {
    dateParamSignal.value = d;
    dispatch(aptUIActions.setCurrentPage(1));
  };

  const _handleChange = (d: moment.Moment | null) => {
    setSelectedDate(d);
    setCalendarDate(d);
    if (d && !datesString.includes(d?.format('DD-MM-YYYY'))) {
      const _dates = getRangeDates(d.format(), d?.clone().add(1, 'month').format());
      setDates(_dates);
      handleScrollDate(d);
      datesListRef.current?.scrollTo({
        left: 0,
      });
    } else {
      afterChangeDate(d);
    }
  };

  const handleClickDate = (_date: string) => {
    const dateValue = moment(_date, 'DD-MM-YYYY');
    setSelectedDate(dateValue);
    setCalendarDate(dateValue);
    afterChangeDate(dateValue);
  };

  useImperativeHandle(ref, () => ({
    setSelectedDate,
    goToDate: _handleChange,
  }));

  const datePickerInput = (
    <WrapDateTime align={'middle'} $compact={compact}>
      <div className='icon-right'><IconCalendar /></div>
      <DatePickerStyled
        $compact={compact}
        value={selectedDate}
        onChange={_handleChange}
        suffixIcon={false}
        clearIcon={false}
        allowClear={false}
        inputReadOnly
        format={'LL'}
        disabledDate={(current: MomentType) => current.isBefore(moment().subtract(1, 'day'))}
      />
    </WrapDateTime>
  );

  if (compact) return datePickerInput;

  return (
    <Row justify={'space-between'} style={{ flex: 1 }}>
      <Row style={{ flex: 1, position: 'relative', height: isModeFull.value ? '65px' : '6rem', }}>
        <Row ref={datesListRef} justify={'space-between'} wrap={false} style={{ position: 'absolute', inset: 0, overflowX: 'scroll' }}>
          <DatesList dates={dates} activeDate={selectedDate?.format('DD-MM-YYYY') || ''} onClickDate={handleClickDate} />
        </Row>
      </Row>
      {datePickerInput}
    </Row>
  );
});
DatePicker.displayName = 'DatePickerRef';
export default DatePicker;

const DatesList = memo(({ onClickDate, activeDate, dates }: { onClickDate: (_date: string) => void, activeDate: string, dates: IDateRangeItem[] }) => {

  return (
    <>
      {dates.map(o => (
        <BoxTimeDate key={o.id}
          style={{
            height: isModeFull.value ? '60px' : '5.5rem',
            justifyContent: isModeFull.value ? 'center' : 'space-between',
          }}
          className={activeDate === o.id ? 'active' : ''}
          onClick={() => onClickDate(o.id)}
          id={'BoxTimeDate_' + o.id}
          type='button'
        >
          {!isModeFull.value && <p>{o.ddd}</p>}
          <p className={`date ${isModeFull.value ? 'mode-full' : ''}`}>{o.D}</p>
        </BoxTimeDate>
      ))}
    </>
  );
});

const WrapDateTime = styled(Row) <{ $compact?: boolean }>`
border-radius: 5px;
border: 1px solid #7B7B7B;
background: #FFF;
display: flex;
height: ${FILTER_INPUT_HEIGHT};
width: 15rem;
justify-content: space-between;
align-items: center;
flex-shrink: 0;
position: relative;
margin: ${({ $compact }) => ($compact ? '0' : '0 1.5rem')};
/* Tren thanh filter, o ngay chia deu voi hai o kia thay vi giu be rong co dinh. O hang tren
   cua che do thuong thi van phai co dinh, vi dai ngay ben canh da chiem het phan co gian. */
${({ $compact }) => $compact && css`
  ${filterInputBox}
  width: auto;
`}

.icon-right {
  position: absolute;
  left: 16px;
  display: flex;
}
.ant-picker .ant-picker-input >input {
  font-size: 1rem;
}
`;

const DatePickerStyled = styled(DatePickerAntUI) <{ $compact?: boolean }>`
  background: none;
  border: none;
  padding:0;
  flex:1;
  
  input {
    ${filterInputText}
    font-size: ${FILTER_INPUT_FONT_SIZE} !important;
    /* Icon lich nam tuyet doi o left:16px, rong 24px.
       Tren thanh filter o da rong bang hai o kia nen chua duoc chu tu 48px, dat ngay sau icon
       cho dong bo. O hang tren che do thuong chi rong 15rem, chua du cho => neo chu ve phai
       la cach duy nhat khong de len icon. */
    text-align: ${({ $compact }) => ($compact ? 'left' : 'right')};
    padding: ${({ $compact }) => ($compact ? '0 16px 0 48px' : '0px 16px')} !important;
  }

  &.ant-picker:hover,
  &.ant-picker-focused {
    border: none;
    box-shadow: none;
  }

`;

const BoxTimeDate = styled.button`
  color: #1D2129;
  text-align: center;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  min-width: 3.5rem;
  margin-right: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  padding-bottom: 8px;
  height: 5.5rem;


  &.active {
    .date {
      border-radius: 100px;
      background: #232F3E;
      color: #fff;
      height: 33px;
      width: 33px;
      display: flex;
      align-items: center;
      justify-content: center;
    }  
  } 

  .date.mode-full {
    height: 50px;
    width: 50px;
    font-size: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:active {
    background: #e9e8e8;
  }
  &:hover {
    background: #e9e8e8;
  }
`;

